/**
 * One person's identity, vendor organization, and grants. Tenant `manage_vendors` changes the
 * organization; tenant `manage_assignments` revokes grants.
 */

import { useCollection } from "@cloudscape-design/collection-hooks"
import Alert from "@cloudscape-design/components/alert"
import Box from "@cloudscape-design/components/box"
import Button from "@cloudscape-design/components/button"
import ColumnLayout from "@cloudscape-design/components/column-layout"
import Container from "@cloudscape-design/components/container"
import ContentLayout from "@cloudscape-design/components/content-layout"
import FormField from "@cloudscape-design/components/form-field"
import Header from "@cloudscape-design/components/header"
import Link from "@cloudscape-design/components/link"
import Modal from "@cloudscape-design/components/modal"
import Pagination from "@cloudscape-design/components/pagination"
import PropertyFilter from "@cloudscape-design/components/property-filter"
import Select from "@cloudscape-design/components/select"
import SpaceBetween from "@cloudscape-design/components/space-between"
import Spinner from "@cloudscape-design/components/spinner"
import StatusIndicator from "@cloudscape-design/components/status-indicator"
import Table from "@cloudscape-design/components/table"
import { useMemo, useState } from "react"
import { useNavigate, useParams } from "react-router-dom"

import type { CapabilitySet } from "@/services/capabilitySetsApi"
import type { PrincipalDetail as Principal, PrincipalGrant } from "@/services/principalsApi"
import type { Vendor } from "@/services/vendorRegistryApi"
import CapabilitySetSummary from "@/components/Access/CapabilitySetSummary"
import { useConfirmedRevoke } from "@/components/Access/useConfirmedRevoke"
import Breadcrumb from "@/components/Navigation/Breadcrumb"
import { useAuth } from "@/contexts/AuthContext"
import { useNotifications } from "@/contexts/NotificationContext"
import { useCapabilitySetsList } from "@/hooks/queries/useCapabilitySets"
import { useMyAccess } from "@/hooks/queries/useMyAccess"
import {
  useAssignPrincipalCompany,
  usePrincipalDetail,
  useRevokePrincipalGrant,
} from "@/hooks/queries/usePrincipals"
import { useRemoveVendorUser, useVendorList } from "@/hooks/queries/useVendors"
import { parseApiErrorMessage } from "@/utils/apiErrors"
import { sameEmail } from "@/utils/email"

const NO_COMPANY = ""
const GRANTS_PER_PAGE = 10

const FILTERING_PROPERTIES = [
  {
    key: "setLabel",
    propertyLabel: "Capability set",
    groupValuesLabel: "Capability sets",
    operators: ["=", "!="],
  },
  {
    key: "resource_id",
    propertyLabel: "Resource",
    groupValuesLabel: "Resources",
    operators: ["=", "!=", ":"],
  },
  {
    key: "resource_type",
    propertyLabel: "Type",
    groupValuesLabel: "Types",
    operators: ["=", "!="],
  },
  {
    key: "granted_by",
    propertyLabel: "Granted by",
    groupValuesLabel: "Granters",
    operators: ["=", "!="],
  },
] as const

const matchText = (n: number) => `${n} ${n === 1 ? "match" : "matches"}`

/**
 * Admin view of the principal in the `principalId` route parameter.
 */
export default function PrincipalDetail() {
  const { principalId = "" } = useParams<{ principalId: string }>()
  const { user } = useAuth()
  const { holdsTenantCapability } = useMyAccess()
  const canManageVendors = holdsTenantCapability("manage_vendors")
  const { data: principal, isPending, error } = usePrincipalDetail(principalId)
  const { sets, isPending: setsPending, error: setsError } = useCapabilitySetsList()
  const { data: vendors = [] } = useVendorList()
  const setsByName = useMemo(() => new Map(sets.map((s) => [s.name, s])), [sets])
  const [changingCompany, setChangingCompany] = useState(false)

  if (isPending || setsPending) {
    return (
      <Box textAlign="center" padding="xxl">
        <Spinner size="large" />
      </Box>
    )
  }
  if (error || setsError || !principal) {
    return (
      <>
        <Breadcrumb items={[{ label: "People", path: "/principals" }, { label: "Not Found" }]} />
        <Alert type="error">
          {parseApiErrorMessage(error ?? setsError, "Failed to load this person")}
        </Alert>
      </>
    )
  }

  // Show the organization row to people in a company or holding an external grant.
  const isVendor =
    principal.company_id !== null ||
    principal.grants.some((g) => setsByName.get(g.capability_set)?.audience === "external")
  const companyName = principal.company_id
    ? (vendors.find((v) => v.vendor_id === principal.company_id)?.vendor_name ??
      principal.company_id)
    : "None"

  return (
    <>
      <Breadcrumb items={[{ label: "People", path: "/principals" }, { label: principal.email }]} />
      <ContentLayout
        header={
          <Header
            variant="h1"
            description="Principal directory entry and grants across all resources"
          >
            {principal.email}
          </Header>
        }
      >
        <SpaceBetween size="l">
          <Container header={<Header variant="h2">Identity</Header>}>
            <ColumnLayout columns={isVendor ? 3 : 2} variant="text-grid">
              <div>
                <Box variant="awsui-key-label">Email</Box>
                <div>{principal.email}</div>
              </div>
              <div>
                <Box variant="awsui-key-label">Status</Box>
                {principal.status === "active" ? (
                  <StatusIndicator type="success">Active</StatusIndicator>
                ) : (
                  <StatusIndicator type="pending">Pending</StatusIndicator>
                )}
              </div>
              {isVendor && (
                <div>
                  <Box variant="awsui-key-label">Organization</Box>
                  <SpaceBetween direction="horizontal" size="xs">
                    <span>{companyName}</span>
                    {canManageVendors && (
                      <Button
                        variant="inline-link"
                        ariaLabel="Change organization"
                        onClick={() => setChangingCompany(true)}
                      >
                        Change
                      </Button>
                    )}
                  </SpaceBetween>
                </div>
              )}
            </ColumnLayout>
          </Container>
          <GrantsContainer
            principal={principal}
            setsByName={setsByName}
            canRevoke={holdsTenantCapability("manage_assignments")}
            userEmail={user?.email}
          />
        </SpaceBetween>
      </ContentLayout>
      {changingCompany && (
        <ChangeCompanyModal
          principal={principal}
          vendors={vendors}
          onClose={() => setChangingCompany(false)}
        />
      )}
    </>
  )
}

/**
 * A principal's grants, with a confirmed revoke per grant.
 */
function GrantsContainer({
  principal,
  setsByName,
  canRevoke,
  userEmail,
}: {
  principal: Principal
  setsByName: ReadonlyMap<string, CapabilitySet>
  canRevoke: boolean
  userEmail: string | undefined
}) {
  const navigate = useNavigate()
  // Only callers with the study pages can open a grant's study or DTA.
  const opensResources = useMyAccess().pages.some((p) => p.page === "studies")
  const revoke = useRevokePrincipalGrant(principal.principal_id)
  const label = (g: PrincipalGrant) =>
    setsByName.get(g.capability_set)?.display_name ?? g.capability_set
  // Carry the set's display name so filters match what the table shows.
  const rows = principal.grants.map((g) => ({ ...g, setLabel: label(g) }))
  const { items, collectionProps, propertyFilterProps, paginationProps, filteredItemsCount } =
    useCollection(rows, {
      propertyFiltering: { filteringProperties: [...FILTERING_PROPERTIES] },
      pagination: { pageSize: GRANTS_PER_PAGE },
    })
  const filteringOptions = FILTERING_PROPERTIES.flatMap(({ key }) =>
    [...new Set(rows.map((r) => r[key]))].map((value) => ({ propertyKey: key, value })),
  )
  const resourceHref = (g: PrincipalGrant) =>
    g.resource_type === "Study" ? `/studies/${g.resource_id}` : `/dtas/${g.resource_id}`

  const { requestRevoke, modal } = useConfirmedRevoke({
    revoke: revoke.mutate,
    describe: (g: PrincipalGrant) => `${label(g)} on ${g.resource_id} from ${principal.email}`,
    revokedMessage: (g: PrincipalGrant) =>
      `${principal.email} no longer has ${label(g)} on ${g.resource_id}`,
  })

  return (
    <>
      <Container
        header={
          <Header
            variant="h2"
            counter={`(${principal.grants.length})`}
            description="Capability sets this person holds across studies and DTAs"
          >
            Grants
          </Header>
        }
      >
        <Table
          {...collectionProps}
          items={items}
          trackBy={(g) => `${g.resource_type}#${g.resource_id}#${g.capability_set}`}
          variant="embedded"
          wrapLines
          empty={
            <Box color="text-body-secondary">
              {rows.length > 0 ? "No grants match the filter." : "This person holds no grants."}
            </Box>
          }
          filter={
            <PropertyFilter
              {...propertyFilterProps}
              filteringOptions={filteringOptions}
              filteringPlaceholder="Filter grants by capability set, resource, type or granter"
              filteringAriaLabel="Filter grants"
              countText={matchText(filteredItemsCount ?? rows.length)}
              expandToViewport
            />
          }
          pagination={<Pagination {...paginationProps} />}
          columnDefinitions={[
            {
              id: "set",
              header: "Capability set",
              cell: (g) => (
                <CapabilitySetSummary
                  name={g.capability_set}
                  set={setsByName.get(g.capability_set)}
                />
              ),
            },
            {
              id: "resource",
              header: "Resource",
              cell: (g) =>
                opensResources ? (
                  <Link
                    href={resourceHref(g)}
                    onFollow={(e) => {
                      e.preventDefault()
                      navigate(resourceHref(g))
                    }}
                  >
                    {g.resource_id}
                  </Link>
                ) : (
                  g.resource_id
                ),
            },
            { id: "type", header: "Type", cell: (g) => g.resource_type },
            { id: "granted_by", header: "Granted by", cell: (g) => g.granted_by },
            { id: "granted_at", header: "Granted", cell: (g) => g.granted_at.slice(0, 10) },
            ...(canRevoke
              ? [
                  {
                    id: "actions",
                    header: "Actions",
                    cell: (g: PrincipalGrant) => (
                      <Button
                        variant="inline-link"
                        disabled={sameEmail(principal.email, userEmail)}
                        disabledReason="You can't revoke your own access"
                        onClick={() => requestRevoke(g)}
                        ariaLabel={`Revoke ${label(g)} on ${g.resource_id}`}
                      >
                        Revoke
                      </Button>
                    ),
                  },
                ]
              : []),
          ]}
        />
      </Container>
      {modal}
    </>
  )
}

/**
 * Set or clear a principal's vendor company.
 */
function ChangeCompanyModal({
  principal,
  vendors,
  onClose,
}: {
  principal: Principal
  vendors: Vendor[]
  onClose: () => void
}) {
  const { showToast } = useNotifications()
  const assign = useAssignPrincipalCompany()
  const remove = useRemoveVendorUser(principal.company_id ?? NO_COMPANY)
  const [companyId, setCompanyId] = useState(principal.company_id ?? NO_COMPANY)
  const [error, setError] = useState<string | null>(null)

  const options = [
    { value: NO_COMPANY, label: "No vendor company" },
    ...vendors.map((v) => ({ value: v.vendor_id, label: v.vendor_name })),
  ]

  const handleSave = async () => {
    setError(null)
    try {
      if (companyId === (principal.company_id ?? NO_COMPANY)) {
        onClose()
        return
      }
      if (companyId === NO_COMPANY) {
        await remove.mutateAsync(principal.principal_id)
      } else {
        await assign.mutateAsync({ principalId: principal.principal_id, companyId })
      }
      showToast({ subject: "Organization updated", message: principal.email, variant: "success" })
      onClose()
    } catch (err) {
      setError(parseApiErrorMessage(err, "Failed to update the organization"))
    }
  }

  return (
    <Modal
      visible
      onDismiss={onClose}
      header={`Organization - ${principal.email}`}
      footer={
        <Box float="right">
          <SpaceBetween direction="horizontal" size="xs">
            <Button variant="link" onClick={onClose}>
              Cancel
            </Button>
            <Button
              variant="primary"
              loading={assign.isPending || remove.isPending}
              onClick={() => void handleSave()}
            >
              Save
            </Button>
          </SpaceBetween>
        </Box>
      }
    >
      <SpaceBetween size="m">
        <FormField
          label="Vendor company"
          description="The person is suggested when granting vendor access on this company's DTAs. Their grants are unchanged."
        >
          <Select
            selectedOption={options.find((o) => o.value === companyId) ?? null}
            onChange={({ detail }) => setCompanyId(detail.selectedOption.value ?? NO_COMPANY)}
            options={options}
            ariaLabel="Vendor company"
          />
        </FormField>
        {error && <Alert type="error">{error}</Alert>}
      </SpaceBetween>
    </Modal>
  )
}
